import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { AuthorizedApiService } from '@core';
import { AuthorizationService } from '@core/service/authorization.service';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import {
  ColumnDefinition,
  MasterTableComponent,
  TableAction,
  TableFilterDefinition,
} from '@shared/components/master-table/master-table.component';
import { ConfirmDeleteDialogComponent } from '@shared/components/confirm-delete-dialog/confirm-delete-dialog.component';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import {
  AttendanceJustificationAttendanceEffect,
  AttendanceJustificationPayEffect,
  AttendanceJustificationScopeAllowed,
  AttendanceJustificationTypeRecord,
} from './attendance-justification-type.interface';

@Component({
  selector: 'app-attendance-justification-type',
  standalone: true,
  imports: [BreadcrumbComponent, MasterTableComponent, TranslateModule],
  templateUrl: './attendance-justification-type.component.html',
  styleUrl: './attendance-justification-type.component.scss',
})
export class AttendanceJustificationTypeComponent implements OnInit {
  dataSource = new MatTableDataSource<AttendanceJustificationTypeRecord>([]);
  isLoading = true;
  actions: TableAction<AttendanceJustificationTypeRecord>[] = [];
  columnDefinitions: ColumnDefinition[] = [];
  tableFilters: TableFilterDefinition<AttendanceJustificationTypeRecord>[] = [];
  searchValue = '';
  activeFilters: Record<string, string | number | boolean | null> = {};

  private readonly apiService = inject(AuthorizedApiService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private searchDebounceHandle: ReturnType<typeof setTimeout> | null = null;

  readonly scopeAllowedOptions: AttendanceJustificationScopeAllowed[] = ['full_day', 'partial_minutes', 'both'];
  readonly payEffectOptions: AttendanceJustificationPayEffect[] = ['paid', 'unpaid'];
  readonly attendanceEffectOptions: AttendanceJustificationAttendanceEffect[] = ['justify_day', 'justify_delay', 'justify_early_exit'];

  ngOnInit(): void {
    this.configureTranslations();
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.configureTranslations();
      this.tableFilters = this.buildTableFilters();
      this.dataSource.data = this.dataSource.data.map((item) => this.normalizeRecord(item));
    });
    void this.loadData();
  }

  async loadData(): Promise<void> {
    this.isLoading = true;
    try {
      const response = await firstValueFrom(
        this.apiService.get<AttendanceJustificationTypeRecord[]>('attendance-justification-types', {
          params: {
            ...(this.searchValue ? { search: this.searchValue } : {}),
            ...(this.getActiveScopeAllowed() ? { scopeAllowed: this.getActiveScopeAllowed() } : {}),
            ...(this.getActiveAttendanceEffect() ? { attendanceEffect: this.getActiveAttendanceEffect() } : {}),
            ...(this.getActiveStatus() !== '' ? { isActive: this.getActiveStatus() } : {}),
          },
        })
      );
      this.dataSource.data = (response || []).map((item) => this.normalizeRecord(item));
      this.tableFilters = this.buildTableFilters();
    } catch (error) {
      console.error('Error loading attendance justification types', error);
      this.dataSource.data = [];
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('attendanceJustificationTypesCatalog.notifications.loadError')),
        'bottom',
        'center'
      );
    } finally {
      this.isLoading = false;
    }
  }

  handleAdd(): void {
    if (!this.hasAccess('create')) {
      return;
    }
    void this.router.navigate(['/attendance/attendance-justification-types/new']);
  }

  async handleView(row: AttendanceJustificationTypeRecord): Promise<void> {
    if (!this.hasAccess('read')) {
      return;
    }
    await this.router.navigate(['/attendance/attendance-justification-types', row.attendanceJustificationTypeId]);
  }

  async handleEdit(row: AttendanceJustificationTypeRecord): Promise<void> {
    if (!this.hasAccess('update')) {
      return;
    }
    await this.router.navigate(['/attendance/attendance-justification-types', row.attendanceJustificationTypeId, 'edit']);
  }

  handleDelete(row: AttendanceJustificationTypeRecord): void {
    if (!this.hasAccess('delete')) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('attendanceJustificationTypesCatalog.dialog.deleteTitle'),
        message: this.t('attendanceJustificationTypesCatalog.dialog.deleteMessage', { justificationType: row.label }),
        confirmLabel: this.t('attendanceJustificationTypesCatalog.dialog.deleteConfirm'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.isLoading = true;
      try {
        await firstValueFrom(this.apiService.delete(`attendance-justification-types/${row.attendanceJustificationTypeId}`));
        await this.loadData();
        this.showNotification(
          'snackbar-success',
          this.t('attendanceJustificationTypesCatalog.notifications.deleted'),
          'bottom',
          'center'
        );
      } catch (error) {
        console.error('Error deleting attendance justification type', error);
        this.showNotification(
          'snackbar-danger',
          extractApiErrorMessage(error, this.t('attendanceJustificationTypesCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isLoading = false;
      }
    });
  }

  handleBulkDelete(rows: AttendanceJustificationTypeRecord[]): void {
    if (!this.hasAccess('delete') || !rows.length) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('common.bulkDeleteTitle'),
        message: this.t('common.bulkDeleteMessage', { count: rows.length }),
        confirmLabel: this.t('common.delete'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.isLoading = true;
      try {
        await Promise.all(
          rows.map((row) =>
            firstValueFrom(this.apiService.delete(`attendance-justification-types/${row.attendanceJustificationTypeId}`))
          )
        );
        await this.loadData();
        this.showNotification('snackbar-success', this.t('common.bulkDeleteSuccess', { count: rows.length }), 'bottom', 'center');
      } catch (error) {
        console.error('Error deleting attendance justification types', error);
        this.showNotification(
          'snackbar-danger',
          extractApiErrorMessage(error, this.t('attendanceJustificationTypesCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isLoading = false;
      }
    });
  }

  onSearchChange(search: string): void {
    this.searchValue = search.trim();
    if (this.searchDebounceHandle) {
      clearTimeout(this.searchDebounceHandle);
    }
    this.searchDebounceHandle = setTimeout(() => {
      void this.loadData();
    }, 300);
  }

  async onFiltersChange(filters: Record<string, string | number | boolean | null>): Promise<void> {
    this.activeFilters = filters;
    this.tableFilters = this.buildTableFilters();
    await this.loadData();
  }

  private normalizeRecord(item: Partial<AttendanceJustificationTypeRecord>): AttendanceJustificationTypeRecord {
    const scopeAllowed = this.normalizeText(item.scopeAllowed) as AttendanceJustificationScopeAllowed || 'full_day';
    const payEffect = this.normalizeText(item.payEffect) as AttendanceJustificationPayEffect || 'paid';
    const attendanceEffect = this.normalizeText(item.attendanceEffect) as AttendanceJustificationAttendanceEffect || 'justify_day';
    const isActive = item.isActive !== false;

    return {
      attendanceJustificationTypeId: item.attendanceJustificationTypeId || crypto.randomUUID(),
      center: item.center || '',
      code: item.code || '',
      label: item.label || '',
      description: item.description || '',
      scopeAllowed,
      requiresApproval: item.requiresApproval === true,
      payEffect,
      attendanceEffect,
      requireComment: item.requireComment === true,
      requireAttachment: item.requireAttachment === true,
      allowEmployeeCreate: item.allowEmployeeCreate === true,
      allowAdminCreate: item.allowAdminCreate === true,
      isActive,
      createdAt: item.createdAt || null,
      updatedAt: item.updatedAt || null,
      scopeAllowedLabel: this.t(`attendanceJustificationTypesCatalog.scopeAllowedOptions.${scopeAllowed}`),
      payEffectLabel: this.t(`attendanceJustificationTypesCatalog.payEffectOptions.${payEffect}`),
      attendanceEffectLabel: this.t(`attendanceJustificationTypesCatalog.attendanceEffectOptions.${attendanceEffect}`),
      requiresApprovalLabel: item.requiresApproval === true ? this.t('common.yes') : this.t('common.no'),
      requireCommentLabel: item.requireComment === true ? this.t('common.yes') : this.t('common.no'),
      requireAttachmentLabel: item.requireAttachment === true ? this.t('common.yes') : this.t('common.no'),
      allowEmployeeCreateLabel: item.allowEmployeeCreate === true ? this.t('common.yes') : this.t('common.no'),
      allowAdminCreateLabel: item.allowAdminCreate === true ? this.t('common.yes') : this.t('common.no'),
      activeLabel: isActive ? this.t('common.status.active') : this.t('common.status.inactive'),
      createdAtLabel: this.formatDateTime(item.createdAt),
      updatedAtLabel: this.formatDateTime(item.updatedAt),
    };
  }

  private buildTableFilters(): TableFilterDefinition<AttendanceJustificationTypeRecord>[] {
    return [
      {
        id: 'scopeAllowed',
        label: this.t('attendanceJustificationTypesCatalog.filters.scopeAllowed'),
        placeholder: this.t('attendanceJustificationTypesCatalog.filters.scopeAllowedPlaceholder'),
        icon: 'calendar_view_week',
        searchable: false,
        value: this.activeFilters['scopeAllowed'] ?? null,
        options: this.scopeAllowedOptions.map((scopeAllowed) => ({
          value: scopeAllowed,
          label: this.t(`attendanceJustificationTypesCatalog.scopeAllowedOptions.${scopeAllowed}`),
        })),
      },
      {
        id: 'attendanceEffect',
        label: this.t('attendanceJustificationTypesCatalog.filters.attendanceEffect'),
        placeholder: this.t('attendanceJustificationTypesCatalog.filters.attendanceEffectPlaceholder'),
        icon: 'rule',
        searchable: false,
        value: this.activeFilters['attendanceEffect'] ?? null,
        options: this.attendanceEffectOptions.map((attendanceEffect) => ({
          value: attendanceEffect,
          label: this.t(`attendanceJustificationTypesCatalog.attendanceEffectOptions.${attendanceEffect}`),
        })),
      },
      {
        id: 'status',
        label: this.t('attendanceJustificationTypesCatalog.fields.status'),
        placeholder: this.t('attendanceJustificationTypesCatalog.filters.statusPlaceholder'),
        icon: 'verified',
        searchable: false,
        value: this.activeFilters['status'] ?? null,
        options: [
          { value: 'true', label: this.t('common.status.active') },
          { value: 'false', label: this.t('common.status.inactive') },
        ],
      },
    ];
  }

  private configureTranslations(): void {
    this.actions = [
      {
        id: 'add',
        icon: 'add',
        tooltip: this.t('attendanceJustificationTypesCatalog.actions.create'),
        colorClass: 'success',
        auth: { resource: 'attendanceJustificationTypes', action: 'create' },
        behavior: 'add',
      },
      {
        id: 'details',
        icon: 'visibility',
        tooltip: this.t('attendanceJustificationTypesCatalog.actions.view'),
        colorClass: 'info',
        auth: { resource: 'attendanceJustificationTypes', action: 'read' },
        selectionMode: 'single',
      },
      {
        id: 'edit',
        icon: 'edit',
        tooltip: this.t('common.update'),
        colorClass: 'accent',
        auth: { resource: 'attendanceJustificationTypes', action: 'update' },
        behavior: 'edit',
        selectionMode: 'single',
      },
      {
        id: 'delete',
        icon: 'delete_outline',
        tooltip: this.t('common.delete'),
        colorClass: 'danger',
        auth: { resource: 'attendanceJustificationTypes', action: 'delete' },
        behavior: 'delete',
        selectionMode: 'single',
      },
    ];

    this.columnDefinitions = [
      { def: 'select', label: this.t('common.select'), type: 'check', visible: true },
      { def: 'label', label: this.t('attendanceJustificationTypesCatalog.fields.label'), type: 'text', visible: true },
      { def: 'code', label: this.t('attendanceJustificationTypesCatalog.fields.code'), type: 'text', visible: true },
      { def: 'scopeAllowedLabel', label: this.t('attendanceJustificationTypesCatalog.fields.scopeAllowed'), type: 'text', visible: true },
      { def: 'attendanceEffectLabel', label: this.t('attendanceJustificationTypesCatalog.fields.attendanceEffect'), type: 'text', visible: true },
      { def: 'payEffectLabel', label: this.t('attendanceJustificationTypesCatalog.fields.payEffect'), type: 'text', visible: true },
      { def: 'requiresApprovalLabel', label: this.t('attendanceJustificationTypesCatalog.fields.requiresApproval'), type: 'status', visible: true },
      { def: 'activeLabel', label: this.t('attendanceJustificationTypesCatalog.fields.status'), type: 'status', visible: true },
    ];

    this.tableFilters = this.buildTableFilters();
  }

  private formatDateTime(value?: string | null): string {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const locale = this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX';
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  private getActiveScopeAllowed(): string {
    return (this.activeFilters['scopeAllowed'] as string) || '';
  }

  private getActiveAttendanceEffect(): string {
    return (this.activeFilters['attendanceEffect'] as string) || '';
  }

  private getActiveStatus(): string {
    const value = this.activeFilters['status'];
    if (value === null || value === undefined || value === '') {
      return '';
    }
    return String(value);
  }

  private normalizeText(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private hasAccess(action: string): boolean {
    return this.authorizationService.hasAccess({ resource: 'attendanceJustificationTypes', action });
  }

  private showNotification(
    colorName: string,
    text: string,
    placementFrom: MatSnackBarVerticalPosition,
    placementAlign: MatSnackBarHorizontalPosition
  ): void {
    this.snackBar.open(text, '', {
      duration: 2400,
      verticalPosition: placementFrom,
      horizontalPosition: placementAlign,
      panelClass: colorName,
    });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }
}
